/**
 * ISG Intranet — internationalisation.
 *
 * This is a thin adapter over the estate's shared engine in
 * `@isg/ui-kit/js/i18n`. It exists so the 826 `t()` call sites across 24 files
 * keep importing `../i18n/index.js` unchanged — the same re-export-shim pattern
 * `app/services/condition_service.py` uses over `isg_common.documents`.
 *
 * What the shared engine replaced here
 * ------------------------------------
 * The previous local implementation had the right idea and the wrong settings:
 * its base locale was `en` (the estate's is `en-GB`), it offered French,
 * Spanish and Italian — which no ISG business speaks — while offering neither
 * American English nor Czech, and all four of its locale files contained
 * literally `{}`. So the Settings language control shipped with one option and
 * would have rendered 100% English had it had more.
 *
 * Plural handling also moved: this app's `polls.votes_one` / `_other` pairs
 * were selected by `count === 1`, which is correct for English and German and
 * wrong for Czech (one / few / many / other). The shared engine uses
 * Intl.PluralRules, so the existing `_one`/`_other` keys keep working and
 * Czech gains the categories it needs.
 */
import {
  initI18n as initShared, registerAppMessages,
  t, getLocale, setLocale, onLocaleChange, getAvailableLocales,
} from '@isg/ui-kit';

import enGB from './en-GB.json';

// ⚠️ AT MODULE SCOPE, not inside initI18n(). main.js builds NAV_CONFIG with
// t('nav.documents') during the import graph, long before any boot function
// runs. Register the base later and every one of those calls returns its own
// key — the sidebar reads "nav.documents" in every language, English included,
// with a clean build and nothing thrown.
registerAppMessages('en-GB', enGB);

export { t, getLocale, setLocale, onLocaleChange, getAvailableLocales };

/**
 * Boot i18n for this app.
 *
 * @param {string|null} serverLocale `user.locale` from isg_core, or null when
 *        signed out. It wins over localStorage: it is the choice that follows
 *        a person between apps and onto other devices.
 */
export function initI18n(serverLocale = null) {
  return initShared({
    base: enGB,
    // Literal template so Vite can glob ./locales/*.json at build time. A
    // computed path here silently produces no chunks and every non-English
    // locale falls back to en-GB with nothing in the build to say why.
    load: (locale) => import(`./locales/${locale}.json`),
    locale: serverLocale,
  });
}
